import { Component, EventEmitter, HostListener, Input, OnInit, Output } from "@angular/core";
import { BaseModalComponent } from "@ha/ui/common";
import { TranslocoService } from "@ngneat/transloco";
import { Store } from "@ngxs/store";
import { PermitCreateActions } from "../../../permit-create/states/permit-create.actions";

export interface WheelDatetimePickerModalComponentData {
    default: string;
    maxValue: number | null;
}

@Component({
    selector: "wheel-datetime-picker",
    styleUrls: ["./wheel-datetime-picker-modal.component.scss"],
    templateUrl: "./wheel-datetime-picker-modal.component.html",
})
export class WheelDatetimePickerModalComponent extends BaseModalComponent implements OnInit {
    public data: WheelDatetimePickerModalComponentData;

    dateOptions: Date[] = [];
    hours: number[] = Array.from({ length: 24 }, (_, i) => i);
    minutes: number[] = Array.from({ length: 12 }, (_, i) => i * 5);

    selectedDateIndex: number = 0;
    selectedHourIndex: number = 0;
    selectedMinuteIndex: number = 0;

    dateOffset = 0;
    hourOffset = 0;
    minuteOffset = 0;

    private isDragging = false;
    private currentType: "date" | "hour" | "minute" | null = null;
    private startY = 0;
    private startOffset = 0;
    private minDateOffset = 0;
    private maxDateOffset = 0;
    private minDate: Date | null = null;
    private maxDate: Date | null = null;
    private result: Date | null = null;
    private now = new Date();

    readonly ITEM_HEIGHT = 40;
    readonly MIDDLE_OFFSET = 80;
    readonly DAYS_TO_SHOW = 30;

    constructor(private translocoService: TranslocoService, private store: Store) {
        super();
    }

    ngOnInit() {
        const today = new Date(this.now.getFullYear(), this.now.getMonth(), this.now.getDate());

        const defaultDate = this.data.default
            ? new Date(this.data.default)
            : new Date(new Date().setHours(new Date().getHours() + 8));
        this.result = defaultDate;
        this.minDate = new Date(new Date().setHours(new Date().getHours() + 1));
        this.maxDate = new Date(new Date().setHours(new Date().getHours() + this.data.maxValue));

        const maxDays = this.data.maxValue ? this.getDaysBetweenDates(this.now, this.maxDate) : this.DAYS_TO_SHOW;

        for (let i = 0; i < maxDays; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            this.dateOptions.push(date);
        }

        this.selectedDateIndex = this.dateOptions.findIndex((date) =>
            date.getFullYear() === defaultDate.getFullYear()
            && date.getMonth() === defaultDate.getMonth()
            && date.getDate() === defaultDate.getDate()
        );
        this.hours = this.getAvailableHours(this.dateOptions[this.selectedDateIndex]);
        this.selectedHourIndex = this.hours.indexOf(defaultDate.getHours());
        if (this.selectedHourIndex === -1) {
            this.selectedHourIndex = 0;
        }
        this.minutes = this.getAvailableMinutes(
            this.dateOptions[this.selectedDateIndex],
            this.hours[this.selectedHourIndex],
        );
        this.selectedMinuteIndex = this.minutes.indexOf(this.getClosestFiveMinutes(defaultDate).getMinutes());
        if (this.selectedMinuteIndex === -1) {
            this.selectedMinuteIndex = 0;
        }

        this.dateOffset = this.calculateOffset(this.selectedDateIndex);
        this.hourOffset = this.calculateOffset(this.selectedHourIndex);
        this.minuteOffset = this.calculateOffset(this.selectedMinuteIndex);

        this.minDateOffset = this.calculateOffset(0);
        this.maxDateOffset = this.calculateOffset(this.dateOptions.length - 1);
        this.updateResult();
    }

    @HostListener("wheel", ["$event"])
    onWheelGlobal(event: WheelEvent) {
        event.preventDefault();
    }

    @HostListener("window:mousemove", ["$event"])
    onMouseMove(event: MouseEvent) {
        if (this.isDragging) {
            event.preventDefault();
            this.handleDragMove(event.clientY);
        }
    }

    @HostListener("window:mouseup")
    onMouseUp() {
        if (this.isDragging) {
            this.finalizeDrag();
        }
    }

    @HostListener("window:touchmove", ["$event"])
    onTouchMove(event: TouchEvent) {
        if (this.isDragging) {
            event.preventDefault();
            this.handleDragMove(event.touches[0].clientY);
        }
    }

    @HostListener("window:touchend")
    onTouchEnd() {
        if (this.isDragging) {
            this.finalizeDrag();
        }
    }

    onDragStart(event: MouseEvent, type: "date" | "hour" | "minute") {
        event.preventDefault();
        this.startDrag(event.clientY, type);
    }

    onTouchStart(event: TouchEvent, type: "date" | "hour" | "minute") {
        event.preventDefault();
        this.startDrag(event.touches[0].clientY, type);
    }

    private startDrag(clientY: number, type: "date" | "hour" | "minute") {
        this.isDragging = true;
        this.currentType = type;
        this.startY = clientY;

        switch (type) {
            case "date":
                this.startOffset = this.dateOffset;
                break;
            case "hour":
                this.startOffset = this.hourOffset;
                break;
            case "minute":
                this.startOffset = this.minuteOffset;
                break;
        }
    }

    private handleDragMove(clientY: number) {
        if (!this.isDragging || !this.currentType) {
            return;
        }

        const delta = this.startY - clientY;

        let newOffset = this.startOffset - delta;

        switch (this.currentType) {
            case "date":
                if (newOffset > this.minDateOffset) {
                    newOffset = this.minDateOffset;
                } else if (newOffset < this.maxDateOffset) {
                    newOffset = this.maxDateOffset;
                }
                this.dateOffset = newOffset;

                break;
            case "hour":
                if (newOffset > this.calculateOffset(0)) {
                    newOffset = this.calculateOffset(0);
                } else if (newOffset < this.calculateOffset(this.hours.length - 1)) {
                    newOffset = this.calculateOffset(this.hours.length - 1);
                }
                this.hourOffset = newOffset;

                break;
            case "minute":
                if (newOffset > this.calculateOffset(0)) {
                    newOffset = this.calculateOffset(0);
                } else if (newOffset < this.calculateOffset(this.minutes.length - 1)) {
                    newOffset = this.calculateOffset(this.minutes.length - 1);
                }
                this.minuteOffset = newOffset;

                break;
        }
    }

    private finalizeDrag() {
        if (!this.currentType) {
            return;
        }

        const getIndexFromOffset = (offset: number) => {
            return Math.round(-(offset - this.MIDDLE_OFFSET) / this.ITEM_HEIGHT);
        };

        switch (this.currentType) {
            case "date":
                const dateIndex = getIndexFromOffset(this.dateOffset);
                if (dateIndex >= 0 && dateIndex < this.dateOptions.length) {
                    this.selectDate(dateIndex);
                }
                break;
            case "hour":
                const hourIndex = getIndexFromOffset(this.hourOffset);
                if (hourIndex >= 0 && hourIndex < 24) {
                    this.selectHour(hourIndex);
                }
                break;
            case "minute":
                const minuteIndex = getIndexFromOffset(this.minuteOffset);
                if (minuteIndex >= 0 && minuteIndex < 12) {
                    this.selectMinute(minuteIndex);
                }
                break;
        }

        this.isDragging = false;
        this.currentType = null;
    }

    calculateOffset(index: number): number {
        return -(index * this.ITEM_HEIGHT) + this.MIDDLE_OFFSET;
    }

    onWheel(event: WheelEvent, type: "date" | "hour" | "minute") {
        event.preventDefault();
        const delta = Math.sign(event.deltaY);

        switch (type) {
            case "date":
                if (delta > 0 && this.selectedDateIndex < this.dateOptions.length - 1) {
                    this.selectDate(this.selectedDateIndex + 1);
                } else if (delta < 0 && this.selectedDateIndex > 0) {
                    this.selectDate(this.selectedDateIndex - 1);
                }
                break;
            case "hour":
                if (delta > 0 && this.selectedHourIndex < this.hours.length - 1) {
                    this.selectHour(this.selectedHourIndex + 1);
                } else if (delta < 0 && this.selectedHourIndex > 0) {
                    this.selectHour(this.selectedHourIndex - 1);
                }
                break;
            case "minute":
                if (delta > 0 && this.selectedMinuteIndex < this.minutes.length - 1) {
                    this.selectMinute(this.selectedMinuteIndex + 1);
                } else if (delta < 0 && this.selectedMinuteIndex > 0) {
                    this.selectMinute(this.selectedMinuteIndex - 1);
                }
                break;
        }
    }

    selectDate(index: number) {
        this.selectedDateIndex = index;
        this.dateOffset = this.calculateOffset(index);

        const selectedDate = this.dateOptions[index];
        this.hours = this.getAvailableHours(selectedDate);

        this.selectedHourIndex = this.hours.indexOf(this.result.getHours());
        if (this.selectedHourIndex === -1) {
            this.selectedHourIndex = 0;
        }

        this.selectHour(this.selectedHourIndex);

        this.updateResult();
    }

    selectHour(index: number) {
        this.selectedHourIndex = index;
        this.hourOffset = this.calculateOffset(index);

        const selectedDate = this.dateOptions[this.selectedDateIndex];
        this.minutes = this.getAvailableMinutes(selectedDate, this.hours[index]);

        this.selectedMinuteIndex = this.minutes.indexOf(this.result.getMinutes());
        if (this.selectedMinuteIndex === -1) {
            this.selectedMinuteIndex = 0;
        }

        this.selectMinute(this.selectedMinuteIndex);
        this.updateResult();
    }

    selectMinute(index: number) {
        this.selectedMinuteIndex = index;
        this.minuteOffset = this.calculateOffset(index);
        this.updateResult();
    }

    formatDate(date: Date): string {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (date.getTime() === today.getTime()) {
            return this.translocoService.translate("permit.today");
        } else {
            return this.getFormattedDate(date);
        }
    }

    private updateResult() {
        const selectedDate = this.dateOptions[this.selectedDateIndex];
        this.result = new Date(selectedDate);
        this.result.setHours(this.hours[this.selectedHourIndex], this.minutes[this.selectedMinuteIndex]);
    }

    getClosestFiveMinutes(date = new Date()) {
        const minutes = date.getMinutes();
        const remainder = minutes % 5;
        const roundedMinutes = remainder === 0 ? minutes : minutes + (5 - remainder);
        date.setMinutes(roundedMinutes);

        return date;
    }

    getFormattedDate(date: Date): string {
        const options: Intl.DateTimeFormatOptions = {
            weekday: "short",
            month: "short",
            day: "2-digit",
        };

        return new Intl.DateTimeFormat(this.translocoService.getActiveLang(), options).format(date);
    }

    private getAvailableHours(selectedDate: Date): number[] {
        const startHour = selectedDate.toDateString() === this.minDate.toDateString() ? this.minDate.getHours() : 0;
        const endHour = selectedDate.toDateString() === this.maxDate.toDateString() ? this.maxDate.getHours() : 23;
        var array = Array.from({ length: endHour - startHour + 1 }, (_, i) => i + startHour);
        return array;
    }

    private getAvailableMinutes(selectedDate: Date, selectedHour: number): number[] {
        if (selectedDate.toDateString() === this.minDate.toDateString() && selectedHour === this.minDate.getHours()) {
            // For minimum date/hour, only show minutes after minDate's minutes
            const startMinute = this.getClosestFiveMinutes(this.minDate).getMinutes();
            return Array.from({ length: Math.floor((60 - startMinute) / 5) }, (_, i) => (i * 5) + startMinute);
        }

        if (selectedDate.toDateString() === this.maxDate.toDateString() && selectedHour === this.maxDate.getHours()) {
            // For maximum date/hour, only show minutes before maxDate's minutes
            const endMinute = Math.floor(this.maxDate.getMinutes() / 5) * 5;
            return Array.from({ length: Math.floor(endMinute / 5) + 1 }, (_, i) => i * 5);
        }

        return Array.from({ length: 12 }, (_, i) => i * 5);
    }

    getDiffInHours(date1: Date, date2: Date): number {
        const diffInMillis = Math.abs(date2.getTime() - date1.getTime()); // Get the absolute difference in milliseconds
        const diffInHours = diffInMillis / (1000 * 60 * 60); // Convert milliseconds to hours
        return Math.floor(diffInHours + 0.5); // Round up to the next whole number
    }

    getDaysBetweenDates(date1: Date, date2: Date): number {
        const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
        const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

        const msPerDay = 1000 * 60 * 60 * 24;
        const diffInMs = d2.getTime() - d1.getTime();

        return Math.abs(diffInMs / msPerDay) + 1;
    }

    close() {
        this.store.dispatch(new PermitCreateActions.SetValidDateTime(this.result.toJSON()));
        this.closeModal();
    }
}
